import styled from "@emotion/styled";

import { Box, Drawer, IconButton, Stack, SxProps } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import Logo from "../icons/Logo";
import { useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material";

const LinkStyled = styled(Link)({
  textDecoration: "none",
  color: "inherit",
  display: "grid",
  placeItems: "center",
  fontWeight: 500,
  fontSize: "1rem",
});

const lightNavigationPaths = new Set([
  "/",
  "/data-provenance-explorer",
  "/data-provenance-explorer/dataset-explorer",
  "/data-provenance-explorer/web-data-explorer",
  "/explorer-introduction",
]);

const explorerShellPaths = new Set([
  "/data-provenance-explorer/dataset-explorer",
  "/data-provenance-explorer/web-data-explorer",
]);

const usesLightNavigation = (path: string) => lightNavigationPaths.has(path);
const usesExplorerShell = (path: string) => explorerShellPaths.has(path);

const LogoLink = () => {
  const location = useLocation();
  const path = location.pathname;

  return (
    <LinkStyled to="/">
      <IconButton
        sx={(theme) => ({
          padding: 0,
          "&>*": {
            width: { xs: "50px", md: "50px" },
            height: { xs: "50px", md: "50px" },
            fill:
              usesLightNavigation(path)
                ? theme.customColors.primary.aliceBlue
                : theme.customColors.primary.prusianBlue,
          },
        })}
      >
        <Logo />
      </IconButton>
    </LinkStyled>
  );
};

const Navbar = ({ sx = {} }: { sx?: SxProps }) => {
  const theme = useTheme();

  const location = useLocation();
  const path = location.pathname;

  return (
    <Box
      className="my-navbar"
      sx={{
        p: 0,
        backgroundColor:
          usesExplorerShell(path)
            ? theme.customColors.primary.prusianBlue
            : "inherit",
        color:
          usesExplorerShell(path)
            ? theme.customColors.primary.aliceBlue
            : "inherit",
        ...sx,
      }}
      component="header"
    >
      <Box
        className="desktop-navbar"
        sx={{
          px: "45px",
          py: "26px",
          display: { xs: "none", lg: "flex" },
          justifyContent: "space-between",
          alignItems: "center",

          [theme.breakpoints.down("lg")]: {
            gridTemplateColumns: "1fr 1fr 1fr",
            justifyItems: "center",
            "&>*.menu-icon": {
              justifySelf: "start",
            },
            "&>*:last-child": {
              justifySelf: "end",
            },
            gap: "10px",
          },
        }}
      >
        <Box
          sx={{
            gap: { xs: "20px", lg: "40px" },
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            [theme.breakpoints.down("lg")]: {
              justifyContent: "space-between",
            },
            ".link": {
              display: { xs: "none", lg: "inline-block" },
            },
          }}
        >
          <LogoLink />

          <LinkStyled
            className="link"
            to="/about"
            style={{ marginLeft: "100px" }}
          >
            About
          </LinkStyled>
        </Box>

        <Stack
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: "75px",
            flexDirection: "row",
            ".link": {
              display: { xs: "none", lg: "inline-block" },
            },
          }}
        >
          <LinkStyled className="link" to="/press">
            Press
          </LinkStyled>
          <LinkStyled className="link" to="/publications">
            Publications
          </LinkStyled>
          <LinkStyled className="link" to="/contributors">
            Team
          </LinkStyled>
          <LinkStyled className="link" to="/faq">
            FAQ
          </LinkStyled>
        </Stack>
      </Box>
      <MobileNavbar />
    </Box>
  );
};

function MobileNavbar() {
  const location = useLocation();
  const path = location.pathname;
  const theme = useTheme();

  const [menuOpen, setMenuOpen] = useState(false);

  function closeDrawer() {
    setMenuOpen(false);
  }

  return (
    <Box
      sx={{
        px: { xs: "0.5rem", sm: "24px" },
        py: { xs: "12px", sm: "21px" },
        display: { xs: "flex", lg: "none" },
        justifyContent: "space-between",
      }}
    >
      <LogoLink />
      <IconButton
        className="menu-icon"
        onClick={() => setMenuOpen((o) => !o)}
        sx={{
          p: 0,
          "*": {
            fontSize: "40px !important",
            fill:
              usesLightNavigation(path)
                ? theme.customColors.primary.aliceBlue
                : theme.customColors.primary.prusianBlue,
          },
        }}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        onClose={() => setMenuOpen(false)}
        anchor="right"
        PaperProps={{
          sx: {
            backgroundColor: theme.customColors.primary.prusianBlue,
            color: theme.customColors.primary.aliceBlue,
            width: "80%",
            zIndex: 2,
            display: { lg: "none" },
          },
        }}
        sx={{
          display: { lg: "none" },
          ".mobile-menu-links": {
            textAlign: "start",
            display: "block",
            fontSize: { md: "32px", sm: "24px", xs: "18px" },
            padding: { md: "24px", sm: "22px", xs: "10px 18px" },
          },
        }}
        open={menuOpen}
      >
        <IconButton
          className="menu-icon"
          onClick={() => setMenuOpen((o) => !o)}
          sx={{
            ml: "auto",
            pr: "2vw",
            mt: "2vw",
            "*": {
              fontSize: "40px",
              color: theme.customColors.primary.aliceBlue,
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ marginTop: "20px" }}>
          <LinkStyled
            onClick={closeDrawer}
            className="mobile-menu-links"
            to={"/about"}
          >
            About
          </LinkStyled>
          <LinkStyled
            onClick={closeDrawer}
            className="mobile-menu-links"
            to={"/press"}
          >
            Press
          </LinkStyled>
          <LinkStyled
            onClick={closeDrawer}
            className="mobile-menu-links"
            to={"/publications"}
          >
            Publications
          </LinkStyled>
          <LinkStyled
            onClick={closeDrawer}
            className="mobile-menu-links"
            to={"/contributors"}
          >
            Team
          </LinkStyled>
          <LinkStyled
            onClick={closeDrawer}
            className="mobile-menu-links"
            to={"/faq"}
          >
            FAQ
          </LinkStyled>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Navbar;
